import React from "react";
import styles from "./WaysToStyleCarousel.scss";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import {
  StylingLookProductFromServer,
  StylingLook,
  StylingLookWithStockPrice,
} from "../../types/productTypes/stylingLookProduct";
import { ProductData } from "../../types/productTypes/product";
import { getPrimaryImage, getHeroProduct } from "../../utils/product";
import Image from "../image/Image";
import PlusPill from "../pills/plusPill/PlusPill";
import LookLabel from "./LookLabel";
import { getLookHeroImageUrl } from "../../utils/heroProductUtils";
import { handleModalTriggerEvent } from "../../utils/handleModalTriggerEvent";
import { WAYS_TO_STYLE_OPEN_MODAL_EVENT } from "../../constants";
import getClientStockPrice from "../../api/clientStockPriceApi";
import {
  mergeStylingLooksWithStockPrice,
  reorderThumbnailsBySelectedStylingLook,
  getDefaultThumbnailIndex,
} from "./utils";
import classnamesBind from "classnames/bind";
import { useWaysToStyle } from "../../context/waysToStyle/WaysToStyleContext";
import { fireWaysToStyleModalOpen } from "../../analytics/waysToStyle/waysToStyleModalOpen";
import eventBusEmitter from "../../utils/eventBusEmitter";

const classnames = classnamesBind.bind(styles);

interface WaysToStyleCarouselProps {
  stylingLooks: StylingLook[];
}

const renderStylingLookHeroImage = (look: StylingLook, index: number) => {
  const heroProduct = getHeroProduct(look.products)[0] ?? look.products[0];

  if (!heroProduct) {
    return null;
  }
  return (
    <Image
      key={`styling-look-hero-${index}`}
      url={getLookHeroImageUrl({
        product: heroProduct,
        looksProducts: look.products,
      })}
      alternateText={heroProduct?.name}
      type="carousel"
      width={640}
      additionalClasses=""
      loadingType="lazy"
      ariaHidden={false}
    />
  );
};

const renderStylingLookThumbnailImage = (
  product: StylingLookProductFromServer,
  index: number
) => (
  <Image
    key={`styling-look-thumbnail-${index}`}
    url={getPrimaryImage(product.images)?.url}
    alternateText={product.name}
    type="thumbnail"
    width={75}
    additionalClasses=""
    loadingType="lazy"
    ariaHidden={false}
  />
);

const getThumbnailsToShow = (
  products: StylingLookProductFromServer[],
  currentProductId?: number
) => {
  const orderedThumbnailProducts = reorderThumbnailsBySelectedStylingLook(
    products,
    currentProductId
  );

  return {
    thumbnailsToShow: orderedThumbnailProducts.slice(0, 4),
    shouldShowPlusPill: orderedThumbnailProducts.length > 4,
    remainingCount: Math.max(0, orderedThumbnailProducts.length - 4),
  };
};

const fetchStockPriceForLook = async (
  look: StylingLook,
  storeCode: string
): Promise<StylingLookWithStockPrice> => {
  const stockPriceData = await getClientStockPrice({
    products: look.products,
    storeCode,
  });

  if (stockPriceData?.length) {
    const mergedLooks = mergeStylingLooksWithStockPrice([look], stockPriceData);
    if (mergedLooks.length) {
      return mergedLooks[0];
    }
  }

  throw new Error("Stock price data unavailable");
};

const fireAndForgetStockPriceForOtherLooks = (
  stylingLooks: StylingLook[],
  storeCode: string,
  clickedIndex: number,
  setStockPriceForLook: (
    lookId: number,
    data: StylingLookWithStockPrice
  ) => void
) => {
  stylingLooks.forEach((look, index) => {
    if (index === clickedIndex) return;

    fetchStockPriceForLook(look, storeCode)
      .then((mergedLook) => {
        setStockPriceForLook(look.id, mergedLook);
      })
      .catch(() => {});
  });
};

export default function WaysToStyleCarousel({
  stylingLooks,
}: WaysToStyleCarouselProps) {
  const { getTranslation, storeCode, product } = useRuntimeConstants() as {
    getTranslation: (key: string) => string;
    storeCode: string;
    product: ProductData;
  };

  const { setStockPriceForLook } = useWaysToStyle();

  if (!stylingLooks?.length) {
    return null;
  }

  const isSingleLook = stylingLooks.length === 1;

  const handleClick =
    (
      lookIndex: number,
      thumbnailsToShow: StylingLookProductFromServer[],
      thumbnailIndex?: number
    ) =>
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      try {
        const defaultThumbnailIndex =
          getDefaultThumbnailIndex(thumbnailsToShow);

        const actualThumbnailIndex = thumbnailIndex ?? defaultThumbnailIndex;

        const mergedLook = await fetchStockPriceForLook(
          stylingLooks[lookIndex],
          storeCode
        );

        setStockPriceForLook(stylingLooks[lookIndex].id, mergedLook);
        fireWaysToStyleModalOpen({
          products: mergedLook.products,
        });

        fireAndForgetStockPriceForOtherLooks(
          stylingLooks,
          storeCode,
          lookIndex,
          setStockPriceForLook
        );

        handleModalTriggerEvent({
          focusOnCloseElement: event.currentTarget,
          eventKey: WAYS_TO_STYLE_OPEN_MODAL_EVENT,
          data: {
            selectedLook: mergedLook,
            lookIndex,
            thumbnailIndex: actualThumbnailIndex,
          },
        });
      } catch {
        eventBusEmitter("notificationBar", {
          type: "error",
          copy: getTranslation("pdp_status_error_technical_difficulties"),
          duration: 5000,
        });
      }
    };

  return (
    <div
      data-testid="waysToStyleCarousel"
      className={styles.carousel}
      aria-label={getTranslation("pdp_buy_the_look_carousel_accessibility")}
    >
      {stylingLooks.map((look, lookIndex) => {
        const { thumbnailsToShow, shouldShowPlusPill, remainingCount } =
          getThumbnailsToShow(look.products, product.id);

        return (
          <div
            key={`${look.products[0]?.id}-${lookIndex}`}
            className={classnames(styles.carouselItem, {
              singleLook: isSingleLook,
              multipleLooks: !isSingleLook,
            })}
          >
            <button
              onClick={handleClick(lookIndex, thumbnailsToShow)}
              className={styles.heroImage}
              data-testid="waysToStyleHeroImageButton"
            >
              {renderStylingLookHeroImage(look, lookIndex)}
              <LookLabel look={look} />
            </button>
            <div className={styles.thumbnailContainer}>
              {thumbnailsToShow.map((product, thumbnailIndex) => (
                <button
                  key={`${product?.id}-${thumbnailIndex}`}
                  onClick={handleClick(
                    lookIndex,
                    thumbnailsToShow,
                    thumbnailIndex
                  )}
                  className={styles.thumbnail}
                  data-testid="waysToStyleThumbnailButton"
                >
                  {renderStylingLookThumbnailImage(product, thumbnailIndex)}
                </button>
              ))}
              {shouldShowPlusPill && (
                <PlusPill
                  number={remainingCount.toString()}
                  additionalClasses={styles.lightTheme}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
